import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { accountService } from "../../Services";
import React, { useEffect, useState } from "react";

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = location.state?.id;
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [verificationData, setVerificationData] = useState(null);

  const sendVerificationEmail = () => {
    const payload = {
      pendingUserId: userId,
      frontend_url: window.location.origin + "/#/account/email-verification",
    };
    accountService
      .emailverification(payload)
      .then((res) => {
        navigate("/account/check-email", {
          state: { email: location.state?.email || "your registered email" },
        });
      })
      .catch((err) => {
        console.error("Email verification error:", err);
        setStatus("error");
        setMessage("Failed to send verification email. Please try again.");
      });
  };

  const verifyToken = () => {
    accountService
      .verifyToken(token)
      .then((res) => {  
        setStatus("verified");
        setMessage("Email verified successfully!");
        setVerificationData(res);
        setShowModal(true); 
      })
      .catch((err) => {
        console.error("Token verification error:", err);
        setStatus("error");
        setMessage(
          typeof err === "string" ? err : err || "Invalid or expired verification link. Please request a new one."
        );
      });
  };

  const handleGoToDashboard = () => {
    const hasSubscription = verificationData?.data?.isUserHasSubscription;
    if (!hasSubscription) {
      navigate("/brand/pricing");
    } else {
      navigate("/brand/dashboard");
    }
  };

  useEffect(() => {
    if (token) {
      verifyToken();
    } else if (userId) {
      sendVerificationEmail();
    } else {
      setStatus("error");
      setMessage(
        "Invalid access. Please register or use the link from your email."
      );
    }
  }, [token, userId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === "loading" && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800">
              Processing...
            </h2>
            <p className="text-gray-600 mt-2">
              Please wait while we verify your email.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Verification Failed
            </h2>
            <p className="text-gray-600 mt-2">{message}</p>
            <button
              onClick={() => navigate("/account/login")}
              className="mt-4 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>

      {/* Verification Success Modal */}
      {showModal && status === "verified" &&(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn">
            {/* Close button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Success Icon with Animation */}
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-800 text-center mb-3">
              🎉 Email Verified Successfully!
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-8">
              Your email has been verified. You can now access all features of your account.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {!verificationData?.data?.isUserHasSubscription
                  ? "View Pricing Plans"
                  : "Go to Dashboard"}
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerification;
